import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AppLanguageCode = 'EN' | 'ES' | 'AR';

export interface AppLanguageOption {
  value: AppLanguageCode;
  name: string;
  profileImage: string;
  dir: 'ltr' | 'rtl';
}

const STORAGE_KEY = 'staff_checkin_language';

const EN: Record<string, string> = {
  // Events
  'events.upcoming': 'Upcoming Events',
  'events.register': 'Register',
  'events.registered': 'Registered',
  'events.waitlisted': 'Waitlisted',
  'events.confirmed': 'Confirmed',
  'events.waitingList': 'Waiting List',
  'events.waitingListCount': 'Waiting List ({count})',
  'events.onWaitingList': 'On Waiting List',
  'events.joinWaitingList': 'Join Waiting List',
  'events.cancelRegistration': 'Cancel Registration',
  'events.spotsLeft': '{count} spots left',
  'events.oneSpotLeft': '1 spot left',
  'events.noSpotsWaitlist': 'No spots left — join the waiting list',
  'events.waitingListHint': 'Waiting list',

  // Common
  'common.back': 'Back',
  'common.save': 'Save',
  'common.where': 'Where',
  'common.when': 'When',
  'common.center': 'Center',
  'common.search': 'Search...',
  'common.dob': 'DOB',
  'common.loading': 'Loading...',

  // Check-in / delivery list
  'checkin.lunch': 'Lunch',
  'checkin.transport': 'Transportation',
  'checkin.activity': 'Activity',
  'checkin.status.all': 'All',
  'checkin.status.yetToCheckIn': 'Scheduled',
  'checkin.status.checkIn': 'Present',
  'checkin.status.delivered': 'Delivered',
  'checkin.status.notDelivered': 'Not Delivered',
  'checkin.status.waitingList': 'Waitlist',
  'checkin.status.noShow': 'No Show',
  'checkin.status.canceled': 'Canceled',
  'checkin.empty.search': 'No People match your search "{query}". Please try a different search term.',
  'checkin.empty.checkIn': 'User list will appear once check-ins are recorded.',
  'checkin.empty.waitingList': 'Waiting list will appear once the Waiting list users have checked in.',
  'checkin.empty.yetToCheckIn': 'Scheduled users will appear when meal appointments are set.',
  'checkin.empty.all': 'Meal schedules will appear once they are created.',
  'checkin.empty.delivered': 'Confirmed deliveries will appear here.',
  'checkin.empty.notDelivered': 'Participants who showed up but did not receive a meal will appear here.',
  'checkin.empty.noShow': 'People who registered for a meal and did not show up will appear here.',
  'checkin.empty.canceled': 'People who canceled after the meal was ordered will appear here.',
  'checkin.empty.generic': 'There are no people available for {status} at this time.',
  'checkin.hint.mealDeliver': 'Tap a participant’s name on Checked In or Waiting List, then choose Delivered or Not present.',
  'checkin.hint.transportDeliver': 'Tap a participant’s name on Checked In or Waiting List, then choose Delivered or Not present.',
  'checkin.hint.activityDeliver': 'Tap a participant’s name on Checked In or Waiting List, then choose Delivered or Not present.',
  'checkin.action.confirmTitle': 'Record delivery',
  'checkin.action.confirmBody': 'How should we record this for {name}?',
  'checkin.action.markDelivered': 'Delivered',
  'checkin.action.markNotPresent': 'Not present',
  'checkin.action.cancel': 'Cancel',
  'checkin.toast.delivered': 'Successfully Delivered!',
  'checkin.toast.notDelivered': 'Marked as Not Delivered (not present).',
  'checkin.empty.transportAll': 'Transportation schedules will appear once approved trips are booked.',
  'checkin.empty.transportCheckIn': 'Approved transportation participants will appear once they check in.',
  'checkin.empty.transportDelivered': 'Confirmed transportation deliveries will appear here.',
  'checkin.empty.transportWaiting': 'Waiting list will appear for approved transportation participants.',
  'checkin.empty.transportYetToCheckIn': 'Scheduled transportation users will appear when trips are set.',
  'checkin.empty.activityAll': 'Activity bookings will appear once seniors register.',
  'checkin.empty.activityCheckIn': 'Seniors with booked activities will appear here for delivery.',
  'checkin.empty.activityDelivered': 'Confirmed activity deliveries will appear here.',
  'checkin.empty.activityWaiting': 'Waiting list will appear for activity participants.',
  'checkin.empty.activityYetToCheckIn': 'Scheduled activity participants will appear when bookings are set.',

  // Card check-in
  'cardCheckin.hint.search': 'To check in, type your name and mobile number, or scan your card',
  'cardCheckin.hint.dob': 'To help us verify your account, please confirm your date of birth.',
  'cardCheckin.placeholder.search': 'Search by Name and Mobile Number',
  'cardCheckin.placeholder.dob': 'Select your Date of Birth',
  'cardCheckin.tapCard': 'Tap your card to Check-In',
  'cardCheckin.verify': 'Verify',
  'cardCheckin.verifying': 'Verifying...',

  // Meal preference
  'meal.title.selectBoth': 'Please Select your Meal Preference and Transportation',
  'meal.title.selectMeal': 'Please Select your Meal Preference',
  'meal.title.selectedBoth': 'Your Selected Meal Preference and Transportation',
  'meal.title.selectedMeal': 'Your Selected Meal Preference',
  'meal.title.noMeal': 'No Meal selected — drink and transportation are not available',
  'meal.section.meal': 'Meal Preference',
  'meal.section.drink': 'Drink Choice',
  'meal.section.transport': 'Transportation',
  'meal.option.hot': 'Hot',
  'meal.option.cold': 'Cold',
  'meal.option.noMeal': 'No Meal',
  'meal.option.milk': 'Milk',
  'meal.option.chocolateMilk': 'Chocolate Milk',
  'meal.option.orangeJuice': 'Orange Juice',
  'meal.option.roundTrip': 'Round Trip',
  'meal.option.toCenter': 'To Center',
  'meal.option.toHome': 'To Home',
  'meal.option.notRequired': 'Not Required',
  'meal.waitlistBanner': "You are on the Waitlist for Today's meal and Transportation",
  'meal.preferredHint': 'You can set this as your preferred choice for the days ahead.',
  'meal.applyFuture': 'Apply for Future',

  // Apply future dates
  'futureDates.title': 'Choose Future Dates',
  'futureDates.subtitle': 'Tap the dates you want to apply your selected meal preference to, then click Apply.',
  'futureDates.empty': 'Upcoming future slots are not yet open.',
  'futureDates.hot': 'Hot',
  'futureDates.cold': 'Cold',
  'futureDates.apply': 'Apply',
  'futureDates.applying': 'Applying...',
  'futureDates.error.missing': 'Preference data is missing. Please select again.',
  'futureDates.error.selectOne': 'Please select at least one future date.',
  'futureDates.error.update': 'Error updating meal info',

  // Success / confirmation
  'success.waitlistBoth': "You are on the Waitlist for Today's meal and Transportation",
  'success.waitlistMeal': "You are on the Waitlist for Today's meal",
  'success.confirmedBoth': "You are confirmed for Today's meal and Transportation Assistance",
  'success.confirmedMeal': "You are confirmed for Today's meal",

  // Header
  'header.logout': 'Logout',
  'header.allYears': 'All years',

  // Programs hub
  'programs.loading': 'Loading...',
  'programs.welcome.before': 'Welcome, ',
  'programs.welcome.after': " You're successfully logged-in",
  'programs.welcomeNamed.before': 'Welcome, ',
  'programs.welcomeNamed.after': ". You're successfully Logged-In",
  'programs.signInRequired': 'Please sign in to continue.',
  'programs.tab.programs': 'Programs',
  'programs.tab.services': 'Services',
  'programs.tab.manageServices': 'Manage Services',
  'programs.card.seniorProgram': 'Senior Program',
  'programs.card.seniorCenterProgram': 'Senior Center Program',
  'programs.openCard': 'Open {title}',
  'programs.areasLabel': 'Program areas',

  // Senior Center tools
  'seniorTools.subtitle': 'Easily check in participants and confirm service deliveries to keep everyone supported and connected',
  'seniorTools.card.checkin.title': 'Self-Service Check-In',
  'seniorTools.card.checkin.description': 'Daily visits to keep you active and connected',
  'seniorTools.card.checkin.aria': 'Go to self-service check-in page',
  'seniorTools.card.delivery.title': 'Service Delivery',
  'seniorTools.card.delivery.description': 'Track and confirm daily service drop-offs for participants.',
  'seniorTools.card.delivery.aria': 'Go to service delivery check-in page',
  'seniorTools.card.applications.title': 'Senior Center Applications',
  'seniorTools.card.applications.description': 'Review and approve Senior Center program applicants.',
  'seniorTools.card.applications.aria': 'Go to Senior Center applications review page',
  'seniorTools.card.staff.title': 'Staff Management',
  'seniorTools.card.staff.description': 'Create, edit, and view staff members with search functionality.',
  'seniorTools.card.staff.aria': 'Go to staff management page',
  'seniorTools.card.registration.title': 'Senior Center Registration',
  'seniorTools.card.registration.description': 'Register and enroll a new participant in the Senior Center program.',
  'seniorTools.card.registration.aria': 'Go to Senior Center registration form',
  'seniorTools.programName': 'Senior Center',

  // Manage senior center
  'manageSenior.title': 'Manage Senior Center Programs',
  'manageSenior.back': 'Back to Manage Service',
  'manageSenior.card.activity': 'Activity',
  'manageSenior.card.mealMenu': 'Meal Menu',
  'manageSenior.card.report': 'Report',
};

const ES: Record<string, string> = {
  'events.upcoming': 'Próximos eventos',
  'events.register': 'Registrarse',
  'events.registered': 'Registrado',
  'events.waitlisted': 'En lista de espera',
  'events.confirmed': 'Confirmado',
  'events.waitingList': 'Lista de espera',
  'events.waitingListCount': 'Lista de espera ({count})',
  'events.onWaitingList': 'En lista de espera',
  'events.joinWaitingList': 'Unirse a la lista de espera',
  'events.cancelRegistration': 'Cancelar registro',
  'events.spotsLeft': '{count} lugares disponibles',
  'events.oneSpotLeft': '1 lugar disponible',
  'events.noSpotsWaitlist': 'Sin lugares — únase a la lista de espera',
  'events.waitingListHint': 'Lista de espera',

  'common.back': 'Atrás',
  'common.save': 'Guardar',
  'common.where': 'Dónde',
  'common.when': 'Cuándo',
  'common.center': 'Centro',
  'common.search': 'Buscar...',
  'common.dob': 'Fecha de nacimiento',
  'common.loading': 'Cargando...',

  'checkin.lunch': 'Almuerzo',
  'checkin.transport': 'Transporte',
  'checkin.activity': 'Actividad',
  'checkin.status.all': 'Todos',
  'checkin.status.yetToCheckIn': 'Pendiente de registro',
  'checkin.status.checkIn': 'Registrado',
  'checkin.status.delivered': 'Entregado',
  'checkin.status.notDelivered': 'No entregado',
  'checkin.status.waitingList': 'Lista de espera',
  'checkin.empty.search': 'Ninguna persona coincide con "{query}". Intente con otro término.',
  'checkin.empty.checkIn': 'La lista aparecerá cuando se registren los usuarios.',
  'checkin.empty.waitingList': 'La lista de espera aparecerá cuando esos usuarios se registren.',
  'checkin.empty.yetToCheckIn': 'Los usuarios programados aparecerán cuando haya citas de comida.',
  'checkin.empty.all': 'Los horarios de comida aparecerán cuando se creen.',
  'checkin.empty.delivered': 'Las entregas confirmadas aparecerán aquí.',
  'checkin.empty.notDelivered': 'Los participantes marcados como no presentes aparecerán aquí.',
  'checkin.empty.generic': 'No hay personas disponibles para {status} en este momento.',
  'checkin.hint.mealDeliver': 'Toque el nombre en Registrado o Lista de espera y elija Entregado o No presente.',
  'checkin.hint.transportDeliver': 'Toque el nombre en Registrado o Lista de espera y elija Entregado o No presente.',
  'checkin.hint.activityDeliver': 'Toque el nombre en Registrado o Lista de espera y elija Entregado o No presente.',
  'checkin.action.confirmTitle': 'Registrar entrega',
  'checkin.action.confirmBody': '¿Cómo registramos a {name}?',
  'checkin.action.markDelivered': 'Entregado',
  'checkin.action.markNotPresent': 'No presente',
  'checkin.action.cancel': 'Cancelar',
  'checkin.toast.delivered': '¡Entregado correctamente!',
  'checkin.toast.notDelivered': 'Marcado como No entregado (no presente).',
  'checkin.empty.transportAll': 'Los horarios de transporte aparecerán cuando haya viajes aprobados.',
  'checkin.empty.transportCheckIn': 'Los participantes con transporte aprobado aparecerán cuando se registren.',
  'checkin.empty.transportDelivered': 'Las entregas de transporte confirmadas aparecerán aquí.',
  'checkin.empty.transportWaiting': 'La lista de espera aparecerá para participantes con transporte aprobado.',
  'checkin.empty.transportYetToCheckIn': 'Los usuarios de transporte programados aparecerán cuando se reserven viajes.',
  'checkin.empty.activityAll': 'Las reservas de actividad aparecerán cuando los seniors se registren.',
  'checkin.empty.activityCheckIn': 'Los seniors con actividades reservadas aparecerán aquí para entrega.',
  'checkin.empty.activityDelivered': 'Las entregas de actividad confirmadas aparecerán aquí.',
  'checkin.empty.activityWaiting': 'La lista de espera aparecerá para participantes de actividad.',
  'checkin.empty.activityYetToCheckIn': 'Los participantes de actividad programados aparecerán cuando haya reservas.',

  'cardCheckin.hint.search': 'Para registrarse, escriba su nombre y número de celular, o escanee su tarjeta',
  'cardCheckin.hint.dob': 'Para verificar su cuenta, confirme su fecha de nacimiento.',
  'cardCheckin.placeholder.search': 'Buscar por nombre y número de celular',
  'cardCheckin.placeholder.dob': 'Seleccione su fecha de nacimiento',
  'cardCheckin.tapCard': 'Toque su tarjeta para registrarse',
  'cardCheckin.verify': 'Verificar',
  'cardCheckin.verifying': 'Verificando...',

  'meal.title.selectBoth': 'Seleccione su preferencia de comida y transporte',
  'meal.title.selectMeal': 'Seleccione su preferencia de comida',
  'meal.title.selectedBoth': 'Su preferencia de comida y transporte seleccionada',
  'meal.title.selectedMeal': 'Su preferencia de comida seleccionada',
  'meal.title.noMeal': 'Sin comida — bebida y transporte no están disponibles',
  'meal.section.meal': 'Preferencia de comida',
  'meal.section.drink': 'Elección de bebida',
  'meal.section.transport': 'Transporte',
  'meal.option.hot': 'Caliente',
  'meal.option.cold': 'Fría',
  'meal.option.noMeal': 'Sin comida',
  'meal.option.milk': 'Leche',
  'meal.option.chocolateMilk': 'Leche con chocolate',
  'meal.option.orangeJuice': 'Jugo de naranja',
  'meal.option.roundTrip': 'Ida y vuelta',
  'meal.option.toCenter': 'Al centro',
  'meal.option.toHome': 'A casa',
  'meal.option.notRequired': 'No requerido',
  'meal.waitlistBanner': 'Está en la lista de espera para la comida y el transporte de hoy',
  'meal.preferredHint': 'Puede establecer esto como su opción preferida para los próximos días.',
  'meal.applyFuture': 'Aplicar a fechas futuras',

  'futureDates.title': 'Elija fechas futuras',
  'futureDates.subtitle': 'Toque las fechas a las que desea aplicar su preferencia de comida, luego haga clic en Aplicar.',
  'futureDates.empty': 'Los horarios futuros aún no están abiertos.',
  'futureDates.hot': 'Caliente',
  'futureDates.cold': 'Fría',
  'futureDates.apply': 'Aplicar',
  'futureDates.applying': 'Aplicando...',
  'futureDates.error.missing': 'Faltan datos de preferencia. Seleccione de nuevo.',
  'futureDates.error.selectOne': 'Seleccione al menos una fecha futura.',
  'futureDates.error.update': 'Error al actualizar la información de la comida',

  'success.waitlistBoth': 'Está en la lista de espera para la comida y el transporte de hoy',
  'success.waitlistMeal': 'Está en la lista de espera para la comida de hoy',
  'success.confirmedBoth': 'Está confirmado para la comida de hoy y la asistencia de transporte',
  'success.confirmedMeal': 'Está confirmado para la comida de hoy',

  'header.logout': 'Cerrar sesión',
  'header.allYears': 'Todos los años',

  'programs.loading': 'Cargando...',
  'programs.welcome.before': 'Bienvenido/a, ',
  'programs.welcome.after': ' Ha iniciado sesión correctamente',
  'programs.welcomeNamed.before': 'Bienvenido/a, ',
  'programs.welcomeNamed.after': '. Ha iniciado sesión correctamente',
  'programs.signInRequired': 'Inicie sesión para continuar.',
  'programs.tab.programs': 'Programas',
  'programs.tab.services': 'Servicios',
  'programs.tab.manageServices': 'Administrar servicios',
  'programs.card.seniorProgram': 'Programa para adultos mayores',
  'programs.card.seniorCenterProgram': 'Programa del centro de adultos mayores',
  'programs.openCard': 'Abrir {title}',
  'programs.areasLabel': 'Áreas del programa',

  'seniorTools.subtitle': 'Registre participantes fácilmente y confirme entregas de servicios para mantener a todos apoyados y conectados',
  'seniorTools.card.checkin.title': 'Autoregistro',
  'seniorTools.card.checkin.description': 'Visitas diarias para mantenerse activo y conectado',
  'seniorTools.card.checkin.aria': 'Ir a la página de autoregistro',
  'seniorTools.card.delivery.title': 'Entrega de servicios',
  'seniorTools.card.delivery.description': 'Rastree y confirme las entregas diarias de servicios para los participantes.',
  'seniorTools.card.delivery.aria': 'Ir a la página de entrega de servicios',
  'seniorTools.card.applications.title': 'Solicitudes del centro de adultos mayores',
  'seniorTools.card.applications.description': 'Revise y apruebe solicitantes del programa del centro de adultos mayores.',
  'seniorTools.card.applications.aria': 'Ir a la página de revisión de solicitudes',
  'seniorTools.card.staff.title': 'Gestión de personal',
  'seniorTools.card.staff.description': 'Cree, edite y vea miembros del personal con función de búsqueda.',
  'seniorTools.card.staff.aria': 'Ir a la página de gestión de personal',
  'seniorTools.card.registration.title': 'Registro del centro de adultos mayores',
  'seniorTools.card.registration.description': 'Registre e inscriba a un nuevo participante en el programa del centro de adultos mayores.',
  'seniorTools.card.registration.aria': 'Ir al formulario de registro del centro de adultos mayores',
  'seniorTools.programName': 'Centro de adultos mayores',

  'manageSenior.title': 'Administrar programas del centro de adultos mayores',
  'manageSenior.back': 'Volver a Administrar servicios',
  'manageSenior.card.activity': 'Actividad',
  'manageSenior.card.mealMenu': 'Menú de comidas',
  'manageSenior.card.report': 'Informe',
};

const AR: Record<string, string> = {
  'events.upcoming': 'الفعاليات القادمة',
  'events.register': 'تسجيل',
  'events.registered': 'مسجل',
  'events.waitlisted': 'في قائمة الانتظار',
  'events.confirmed': 'مؤكد',
  'events.waitingList': 'قائمة الانتظار',
  'events.waitingListCount': 'قائمة الانتظار ({count})',
  'events.onWaitingList': 'في قائمة الانتظار',
  'events.joinWaitingList': 'الانضمام إلى قائمة الانتظار',
  'events.cancelRegistration': 'إلغاء التسجيل',
  'events.spotsLeft': '{count} أماكن متبقية',
  'events.oneSpotLeft': 'مكان واحد متبقٍ',
  'events.noSpotsWaitlist': 'لا توجد أماكن — انضم إلى قائمة الانتظار',
  'events.waitingListHint': 'قائمة الانتظار',

  'common.back': 'رجوع',
  'common.save': 'حفظ',
  'common.where': 'أين',
  'common.when': 'متى',
  'common.center': 'المركز',
  'common.search': 'بحث...',
  'common.dob': 'تاريخ الميلاد',
  'common.loading': 'جارٍ التحميل...',

  'checkin.lunch': 'الغداء',
  'checkin.transport': 'النقل',
  'checkin.activity': 'النشاط',
  'checkin.status.all': 'الكل',
  'checkin.status.yetToCheckIn': 'لم يتم تسجيل الدخول',
  'checkin.status.checkIn': 'تم تسجيل الدخول',
  'checkin.status.delivered': 'تم التسليم',
  'checkin.status.notDelivered': 'لم يتم التسليم',
  'checkin.status.waitingList': 'قائمة الانتظار',
  'checkin.empty.search': 'لا يوجد أشخاص يطابقون "{query}". جرّب بحثًا آخر.',
  'checkin.empty.checkIn': 'ستظهر القائمة بعد تسجيل الدخول.',
  'checkin.empty.waitingList': 'ستظهر قائمة الانتظار بعد تسجيل هؤلاء المستخدمين.',
  'checkin.empty.yetToCheckIn': 'سيظهر المستخدمون المجدولون عند تحديد مواعيد الوجبات.',
  'checkin.empty.all': 'ستظهر جداول الوجبات بعد إنشائها.',
  'checkin.empty.delivered': 'ستظهر عمليات التسليم المؤكدة هنا.',
  'checkin.empty.notDelivered': 'سيظهر هنا المشاركون الذين تم تعليمهم غير موجودين.',
  'checkin.empty.generic': 'لا يوجد أشخاص متاحون لـ {status} في الوقت الحالي.',
  'checkin.hint.mealDeliver': 'انقر على الاسم في تم تسجيل الدخول أو قائمة الانتظار ثم اختر تم التسليم أو غير موجود.',
  'checkin.hint.transportDeliver': 'انقر على الاسم في تم تسجيل الدخول أو قائمة الانتظار ثم اختر تم التسليم أو غير موجود.',
  'checkin.hint.activityDeliver': 'انقر على الاسم في تم تسجيل الدخول أو قائمة الانتظار ثم اختر تم التسليم أو غير موجود.',
  'checkin.action.confirmTitle': 'تسجيل التسليم',
  'checkin.action.confirmBody': 'كيف نسجل {name}؟',
  'checkin.action.markDelivered': 'تم التسليم',
  'checkin.action.markNotPresent': 'غير موجود',
  'checkin.action.cancel': 'إلغاء',
  'checkin.toast.delivered': 'تم التسليم بنجاح!',
  'checkin.toast.notDelivered': 'تم التعليم كـ لم يتم التسليم (غير موجود).',
  'checkin.empty.transportAll': 'ستظهر جداول النقل بعد حجز الرحلات المعتمدة.',
  'checkin.empty.transportCheckIn': 'سيظهر المشاركون المعتمدون للنقل بعد تسجيل الدخول.',
  'checkin.empty.transportDelivered': 'ستظهر عمليات توصيل النقل المؤكدة هنا.',
  'checkin.empty.transportWaiting': 'ستظهر قائمة الانتظار للمشاركين المعتمدين للنقل.',
  'checkin.empty.transportYetToCheckIn': 'سيظهر مستخدمو النقل المجدولون عند تحديد الرحلات.',
  'checkin.empty.activityAll': 'ستظهر حجوزات النشاط بعد تسجيل كبار السن.',
  'checkin.empty.activityCheckIn': 'سيظهر كبار السن الذين حجزوا أنشطة هنا للتسليم.',
  'checkin.empty.activityDelivered': 'ستظهر عمليات تسليم النشاط المؤكدة هنا.',
  'checkin.empty.activityWaiting': 'ستظهر قائمة الانتظار لمشاركي النشاط.',
  'checkin.empty.activityYetToCheckIn': 'سيظهر مشاركو النشاط المجدولون عند وجود حجوزات.',

  'cardCheckin.hint.search': 'لتسجيل الدخول، اكتب اسمك ورقم هاتفك أو امسح بطاقتك',
  'cardCheckin.hint.dob': 'للتحقق من حسابك، يرجى تأكيد تاريخ ميلادك.',
  'cardCheckin.placeholder.search': 'البحث بالاسم ورقم الهاتف',
  'cardCheckin.placeholder.dob': 'اختر تاريخ ميلادك',
  'cardCheckin.tapCard': 'المس بطاقتك لتسجيل الدخول',
  'cardCheckin.verify': 'تحقق',
  'cardCheckin.verifying': 'جارٍ التحقق...',

  'meal.title.selectBoth': 'يرجى اختيار تفضيل الوجبة والنقل',
  'meal.title.selectMeal': 'يرجى اختيار تفضيل الوجبة',
  'meal.title.selectedBoth': 'تفضيل الوجبة والنقل المحدد',
  'meal.title.selectedMeal': 'تفضيل الوجبة المحدد',
  'meal.title.noMeal': 'بدون وجبة — المشروب والنقل غير متاحين',
  'meal.section.meal': 'تفضيل الوجبة',
  'meal.section.drink': 'اختيار المشروب',
  'meal.section.transport': 'النقل',
  'meal.option.hot': 'ساخنة',
  'meal.option.cold': 'باردة',
  'meal.option.noMeal': 'بدون وجبة',
  'meal.option.milk': 'حليب',
  'meal.option.chocolateMilk': 'حليب بالشوكولاتة',
  'meal.option.orangeJuice': 'عصير برتقال',
  'meal.option.roundTrip': 'ذهاب وعودة',
  'meal.option.toCenter': 'إلى المركز',
  'meal.option.toHome': 'إلى المنزل',
  'meal.option.notRequired': 'غير مطلوب',
  'meal.waitlistBanner': 'أنت في قائمة الانتظار لوجبة اليوم والنقل',
  'meal.preferredHint': 'يمكنك تعيين هذا كخيارك المفضل للأيام القادمة.',
  'meal.applyFuture': 'تطبيق على تواريخ مستقبلية',

  'futureDates.title': 'اختر تواريخ مستقبلية',
  'futureDates.subtitle': 'المس التواريخ التي تريد تطبيق تفضيل الوجبة عليها، ثم انقر على تطبيق.',
  'futureDates.empty': 'الفترات المستقبلية غير مفتوحة بعد.',
  'futureDates.hot': 'ساخنة',
  'futureDates.cold': 'باردة',
  'futureDates.apply': 'تطبيق',
  'futureDates.applying': 'جارٍ التطبيق...',
  'futureDates.error.missing': 'بيانات التفضيل مفقودة. يرجى الاختيار مرة أخرى.',
  'futureDates.error.selectOne': 'يرجى اختيار تاريخ مستقبلي واحد على الأقل.',
  'futureDates.error.update': 'خطأ في تحديث معلومات الوجبة',

  'success.waitlistBoth': 'أنت في قائمة الانتظار لوجبة اليوم والنقل',
  'success.waitlistMeal': 'أنت في قائمة الانتظار لوجبة اليوم',
  'success.confirmedBoth': 'تم تأكيدك لوجبة اليوم ومساعدة النقل',
  'success.confirmedMeal': 'تم تأكيدك لوجبة اليوم',

  'header.logout': 'تسجيل الخروج',
  'header.allYears': 'كل السنوات',

  'programs.loading': 'جارٍ التحميل...',
  'programs.welcome.before': 'مرحبًا، ',
  'programs.welcome.after': ' تم تسجيل دخولك بنجاح',
  'programs.welcomeNamed.before': 'مرحبًا، ',
  'programs.welcomeNamed.after': '. تم تسجيل دخولك بنجاح',
  'programs.signInRequired': 'يرجى تسجيل الدخول للمتابعة.',
  'programs.tab.programs': 'البرامج',
  'programs.tab.services': 'الخدمات',
  'programs.tab.manageServices': 'إدارة الخدمات',
  'programs.card.seniorProgram': 'برنامج كبار السن',
  'programs.card.seniorCenterProgram': 'برنامج مركز كبار السن',
  'programs.openCard': 'فتح {title}',
  'programs.areasLabel': 'مجالات البرنامج',

  'seniorTools.subtitle': 'سجّل حضور المشاركين بسهولة وأكّد توصيل الخدمات لإبقاء الجميع مدعومين ومتصلين',
  'seniorTools.card.checkin.title': 'تسجيل الدخول الذاتي',
  'seniorTools.card.checkin.description': 'زيارات يومية للبقاء نشيطًا ومتصلًا',
  'seniorTools.card.checkin.aria': 'الانتقال إلى صفحة تسجيل الدخول الذاتي',
  'seniorTools.card.delivery.title': 'تقديم الخدمات',
  'seniorTools.card.delivery.description': 'تتبّع وأكّد عمليات التوصيل اليومية للمشاركين.',
  'seniorTools.card.delivery.aria': 'الانتقال إلى صفحة تقديم الخدمات',
  'seniorTools.card.applications.title': 'طلبات مركز كبار السن',
  'seniorTools.card.applications.description': 'راجع واعتمد المتقدمين لبرنامج مركز كبار السن.',
  'seniorTools.card.applications.aria': 'الانتقال إلى صفحة مراجعة الطلبات',
  'seniorTools.card.staff.title': 'إدارة الموظفين',
  'seniorTools.card.staff.description': 'أنشئ وعدّل واعرض أعضاء الموظفين مع إمكانية البحث.',
  'seniorTools.card.staff.aria': 'الانتقال إلى صفحة إدارة الموظفين',
  'seniorTools.card.registration.title': 'تسجيل مركز كبار السن',
  'seniorTools.card.registration.description': 'سجّل وأدرج مشاركًا جديدًا في برنامج مركز كبار السن.',
  'seniorTools.card.registration.aria': 'الانتقال إلى نموذج تسجيل مركز كبار السن',
  'seniorTools.programName': 'مركز كبار السن',

  'manageSenior.title': 'إدارة برامج مركز كبار السن',
  'manageSenior.back': 'العودة إلى إدارة الخدمات',
  'manageSenior.card.activity': 'النشاط',
  'manageSenior.card.mealMenu': 'قائمة الوجبات',
  'manageSenior.card.report': 'التقرير',
};

const DICTIONARIES: Record<AppLanguageCode, Record<string, string>> = {
  EN,
  ES,
  AR,
};

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly languages: AppLanguageOption[] = [
    { value: 'EN', name: 'EN', profileImage: 'https://flagcdn.com/w40/us.png', dir: 'ltr' },
    { value: 'ES', name: 'ES', profileImage: 'https://flagcdn.com/w40/es.png', dir: 'ltr' },
    { value: 'AR', name: 'AR', profileImage: 'https://flagcdn.com/w40/sa.png', dir: 'rtl' },
  ];

  private readonly languageSubject = new BehaviorSubject<AppLanguageCode>(this.readStoredLanguage());
  readonly language$ = this.languageSubject.asObservable();

  constructor() {
    this.applyDocumentDirection(this.languageSubject.value);
  }

  get currentLanguage(): AppLanguageCode {
    return this.languageSubject.value;
  }

  get locale(): string {
    switch (this.currentLanguage) {
      case 'ES':
        return 'es';
      case 'AR':
        return 'ar';
      default:
        return 'en-US';
    }
  }

  setLanguage(code: AppLanguageCode | string): void {
    const normalized = String(code || 'EN').toUpperCase() as AppLanguageCode;
    const matched = this.languages.find((lang) => lang.value === normalized);
    const next = matched?.value ?? 'EN';
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    this.languageSubject.next(next);
    this.applyDocumentDirection(next);
  }

  t(key: string, params?: Record<string, string | number>): string {
    const dict = DICTIONARIES[this.currentLanguage] ?? DICTIONARIES.EN;
    let value = dict[key] ?? DICTIONARIES.EN[key] ?? key;
    if (params) {
      for (const [paramKey, paramValue] of Object.entries(params)) {
        value = value.replace(`{${paramKey}}`, String(paramValue));
      }
    }
    return value;
  }

  private readStoredLanguage(): AppLanguageCode {
    try {
      const raw = (localStorage.getItem(STORAGE_KEY) || 'EN').toUpperCase();
      if (raw === 'ES' || raw === 'AR' || raw === 'EN') {
        return raw;
      }
    } catch {
      /* ignore */
    }
    return 'EN';
  }

  private applyDocumentDirection(code: AppLanguageCode): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.lang = code.toLowerCase();
    // Keep the staff portal chrome LTR always. Arabic translations must not flip the
    // whole layout (logo / date / location / language / logout) via document.dir=rtl.
    document.documentElement.dir = 'ltr';
  }
}
